import { useMemo, useState } from "react";
import { categories, events, type EventItem, type Category } from "@/mocks/events";

function statusCls(status: string): string {
  if (status === "发酵中") return "bg-accent-100 text-accent-800";
  if (status === "已反转") return "bg-secondary-100 text-secondary-800";
  if (status === "有新进展") return "bg-primary-100 text-primary-700";
  return "bg-background-200 text-foreground-600";
}

function kindDot(kind: string): string {
  if (kind === "开始") return "bg-background-400";
  if (kind === "发酵") return "bg-accent-500";
  if (kind === "高潮") return "bg-primary-500";
  if (kind === "反转") return "bg-secondary-500";
  if (kind === "进展") return "bg-primary-400";
  return "bg-foreground-400";
}

export default function TimelineBoard() {
  const [active, setActive] = useState<string>("all");
  const [selected, setSelected] = useState<EventItem | null>(null);

  const list = useMemo(
    () => (active === "all" ? events : events.filter((e) => e.category === active)),
    [active]
  );

  return (
    <section id="timeline" className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
      <div className="text-center">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1 text-xs font-bold text-accent-800">
          <i className="ri-git-branch-line text-sm"></i>
          关键节点
        </span>
        <h2 className="mt-3 font-heading text-2xl font-black text-foreground-950 md:text-3xl">事件时间线全记录</h2>
        <p className="mx-auto mt-2 max-w-2xl text-sm text-foreground-600">
          从爆发到发酵再到反转，点击任意事件展开完整脉络，关键节点一目了然。
        </p>
      </div>

      <div className="mt-7 flex flex-wrap justify-center gap-2">
        {categories.map((c: Category) => (
          <button
            key={c.key}
            type="button"
            onClick={() => setActive(c.key)}
            className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
              active === c.key
                ? "bg-primary-500 text-background-50"
                : "bg-background-100 text-foreground-700 hover:bg-background-200"
            }`}
          >
            <i className={`${c.icon} text-sm`}></i>
            {c.name}
          </button>
        ))}
      </div>

      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {list.map((e) => (
          <article
            key={e.id}
            onClick={() => setSelected(e)}
            className={`group flex cursor-pointer flex-col rounded-2xl border p-5 transition-all hover:-translate-y-0.5 ${
              e.highlight
                ? "border-primary-200 bg-primary-50/60 hover:border-primary-300"
                : "border-background-200 bg-background-50 hover:border-background-300"
            }`}
          >
            <div className="flex items-center justify-between gap-2">
              <span className={`whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-bold ${statusCls(e.status)}`}>
                {e.status}
              </span>
              <span className="text-xs text-foreground-500">{e.date}</span>
            </div>
            <h3 className="mt-3 font-heading text-base font-extrabold leading-snug text-foreground-950 md:text-lg">
              {e.title}
            </h3>
            <p className="mt-2 flex-1 text-sm leading-relaxed text-foreground-600">{e.brief}</p>

            <div className="mt-4 flex items-center gap-1 overflow-hidden">
              {e.nodes.map((n) => (
                <i key={n.time} className={`h-1.5 flex-1 rounded-full ${kindDot(n.kind)}`}></i>
              ))}
            </div>

            <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-3">
              <span className="flex items-center gap-3 text-xs text-foreground-500">
                <span className="flex items-center gap-1"><i className="ri-time-line text-sm"></i>{e.readTime}</span>
                <span className="flex items-center gap-1"><i className="ri-message-3-line text-sm"></i>{e.comments}</span>
              </span>
              <span className="flex items-center gap-1 text-xs font-bold text-primary-600 transition-colors group-hover:text-primary-700">
                看全记录 <i className="ri-arrow-right-line text-sm"></i>
              </span>
            </div>
          </article>
        ))}
      </div>

      {selected && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center overflow-y-auto bg-foreground-950/60 p-4 backdrop-blur-sm"
          onClick={() => setSelected(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative my-6 w-full max-w-2xl rounded-2xl bg-background-50 p-6 shadow-2xl md:p-8"
          >
            <button
              type="button"
              aria-label="关闭详情"
              onClick={() => setSelected(null)}
              className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-background-100 text-foreground-600 transition-colors hover:bg-background-200"
            >
              <i className="ri-close-line text-lg"></i>
            </button>

            <span className={`inline-flex whitespace-nowrap rounded-full px-3 py-1 text-xs font-bold ${statusCls(selected.status)}`}>
              {selected.status}
            </span>
            <h3 className="mt-3 pr-8 font-heading text-xl font-black leading-snug text-foreground-950 md:text-2xl">
              {selected.title}
            </h3>
            <p className="mt-2 text-sm text-foreground-600">{selected.brief}</p>

            <div className="relative mt-7">
              <span className="absolute bottom-2 left-[13px] top-2 w-0.5 bg-background-200"></span>
              <ol className="space-y-6">
                {selected.nodes.map((n) => (
                  <li key={n.time} className="relative flex gap-4 pl-1">
                    <span className={`relative z-10 mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-4 border-background-50 ${kindDot(n.kind)}`}>
                      <i className="ri-check-line text-xs text-background-50"></i>
                    </span>
                    <div className="pb-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs font-bold text-foreground-500">{n.time}</span>
                        <span className="text-[11px] font-medium text-foreground-400">{n.kind}</span>
                      </div>
                      <h4 className="mt-1 text-sm font-extrabold text-foreground-950 md:text-base">{n.title}</h4>
                      <p className="mt-1 text-sm leading-relaxed text-foreground-600">{n.detail}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}